import { useCallback } from 'react';
import { atom, useRecoilState } from 'recoil';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpApi from 'i18next-http-backend';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import 'dayjs/locale/nl';
import 'dayjs/locale/ru';
import 'dayjs/locale/uk';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/zh-tw';
import 'dayjs/locale/vi';
import 'dayjs/locale/ja';
import 'dayjs/locale/tr';
import 'dayjs/locale/id';
import 'dayjs/locale/ko';
import 'dayjs/locale/pl';
import 'dayjs/locale/es';
import 'dayjs/locale/it';
import 'dayjs/locale/pt';
import 'dayjs/locale/bg';

import { getRecoil } from 'recoil-nexus';
import { richEditorI18n } from '@debank/content-editor';

import { SUPPORTED_LOCALES } from '@/locales';

import deLngJson from '@/locales/de/common.json';
import enLngJson from '@/locales/en/common.json';
import frLngJson from '@/locales/fr/common.json';
import nlLngJson from '@/locales/nl/common.json';
import ruLngJson from '@/locales/ru/common.json';
import ukLngJson from '@/locales/uk/common.json';
import zhCNLngJson from '@/locales/zh-cn/common.json';
import zhTwLngJson from '@/locales/zh-tw/common.json';
import viLngJson from '@/locales/vi/common.json';
import jaLngJson from '@/locales/ja/common.json';
import trLngJson from '@/locales/tr/common.json';
import idLngJson from '@/locales/id/common.json';
import koLngJson from '@/locales/ko/common.json';
import plLngJson from '@/locales/pl/common.json';
import esLngJson from '@/locales/es/common.json';
import itLngJson from '@/locales/it/common.json';
import ptLngJson from '@/locales/pt/common.json';
import bgLngJson from '@/locales/bg/common.json';

import storage from './storage1';
import env from './env';
import { localStorageEffect, registerSyncAtom } from './recoil';

// 兼容已经设置locale的用户的逻辑，一段时间后可以下掉
if (storage.auto_translate_UGC === null && storage.locale) {
  localStorage.setItem('auto_translate_UGC', 'true');
  storage.auto_translate_UGC = true;
}

const defaultLng = storage.locale || 'en';

const localeState = atom({
  key: 'localeState',
  default: storage.locale,
  effects_UNSTABLE: [
    localStorageEffect('locale'),
    ({ onSet }) => {
      onSet((next: string) => {
        // 其他 tab 的刷新
        window.location.reload();
      });
    },
  ],
});

registerSyncAtom('locale', localeState);

const backendResources = {
  resources: {
    de: { common: deLngJson },
    en: { common: enLngJson },
    fr: { common: frLngJson },
    nl: { common: nlLngJson },
    ru: { common: ruLngJson },
    uk: { common: ukLngJson },
    'zh-cn': { common: zhCNLngJson },
    'zh-tw': { common: zhTwLngJson },
    vi: { common: viLngJson },
    ja: { common: jaLngJson },
    tr: { common: trLngJson },
    id: { common: idLngJson },
    ko: { common: koLngJson },
    pl: { common: plLngJson },
    es: { common: esLngJson },
    it: { common: itLngJson },
    pt: { common: ptLngJson },
    bg: { common: bgLngJson },
  },
};

const I18N_NS = 'common';

const httpBackendOptions = {
  backend: {
    loadPath: (lngs: string[], nss: string[]) => {
      // since we are not configured for multi-resource lookup, lngs and nss will only have 1 item each
      const lookupLng = lngs?.[0] as keyof (typeof backendResources)['resources'];
      const lookupNs = nss?.[0] as typeof I18N_NS;

      return backendResources.resources[lookupLng]?.[lookupNs];
    },
  },
};

const backendOptions = env.isTest ? backendResources : httpBackendOptions;

let initFn = i18n.use(initReactI18next);

if (!env.isTest) {
  initFn = initFn.use(HttpApi);
}

initFn.init({
  fallbackLng: defaultLng,
  ns: [I18N_NS],
  defaultNS: I18N_NS,
  lowerCaseLng: true,
  supportedLngs: SUPPORTED_LOCALES.filter(x => x.key).map(x => x.key),
  ...backendOptions,
});

dayjs.locale(defaultLng);
richEditorI18n.changeLanguage(defaultLng);

export const useLocale = () => {
  const [l, setL] = useRecoilState(localeState);

  const setCurrentLocale = useCallback(
    async (lng: string) => {
      const target = SUPPORTED_LOCALES.find(x => x.key === lng);
      if (target && target.key !== l) {
        dayjs.locale(target.key);
        i18n.changeLanguage(target.key);
        richEditorI18n.changeLanguage(target.key);
        setL(target.key);
      }
    },
    [setL, l],
  );

  return [l || '', setCurrentLocale];
};

export const getCurrentLng = () => getRecoil(localeState);

// 控制是否自动翻译UGC内容
const AutoTranslateUGC = atom({
  key: 'autoTranslateUGC',
  default: storage.auto_translate_UGC,
  effects_UNSTABLE: [
    localStorageEffect('auto_translate_UGC'),
    ({ onSet }) => {
      onSet((next: string) => {
        // 其他 tab 的刷新
        window.location.reload();
      });
    },
  ],
});
registerSyncAtom('auto_translate_UGC', AutoTranslateUGC);

export const useAutoTranslateUGC = () => {
  const [autoTranslateUGC, setAutoTranslateUGC] = useRecoilState(AutoTranslateUGC);
  return { autoTranslateUGC, setAutoTranslateUGC };
};

export { initFn as i18nInstance };
